import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@/lib/apiClient'
import storage from '@/lib/storage'
import { supportEmail, supportTelegram } from '@/lib/legal'
import { telegramDirectUrl } from '@/lib/telegram'
import { useSessionStore } from '@/store/session'
import { Button } from '@/components/ui/Button'
import { Icon } from '@/components/ui/Icon'
import { Notice } from '@/components/ui/Feedback'
import { supportChatApi, type ChatMessage, type ChatThread } from './api'
import { TelegramLink } from './TelegramLink'

/** What the browser keeps of a visitor's chat; an account's chat is read from the server by its session. */
type Visitor = { token: string; conversationId: string }
const visitorKey = 'bspos-support-chat'
const when = new Intl.DateTimeFormat('es-MX', { hour: '2-digit', minute: '2-digit' })

function readVisitor(): Visitor | null {
  try {
    const saved = storage.getItem(visitorKey)
    const parsed = saved ? JSON.parse(saved) as Partial<Visitor> : null
    return parsed?.token && parsed.conversationId ? { token: parsed.token, conversationId: parsed.conversationId } : null
  } catch {
    return null
  }
}

/**
 * The support chat on every page but the administration: a visitor writes without an account and keeps a token in
 * the browser; an account writes as itself and takes the visitor's conversation of the same browser when it signs
 * in. Every answer comes from a person, or was approved by one, so the panel says it may take a while.
 */
export function SupportChatWidget() {
  const status = useSessionStore((state) => state.status)
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [visitor, setVisitor] = useState<Visitor | null>(readVisitor)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [claimed, setClaimed] = useState(false)
  const messageId = useRef(crypto.randomUUID())
  const panelId = useId()
  const headingId = useId()
  const fieldId = useId()
  const endOfThread = useRef<HTMLDivElement>(null)
  const field = useRef<HTMLTextAreaElement>(null)
  const signedIn = status === 'authenticated'
  const queryKey = signedIn ? ['support-chat', 'me'] : ['support-chat', 'visitor', visitor?.conversationId ?? null]

  const thread = useQuery<ChatThread>({
    queryKey,
    enabled: open && (signedIn || visitor !== null),
    queryFn: () => signedIn ? supportChatApi.mine() : supportChatApi.read(visitor!.conversationId, visitor!.token),
    refetchInterval: open ? 5000 : false,
    retry: false,
  })

  // An account that signs in from a visitor's browser takes that visitor's conversation, once.
  useEffect(() => {
    if (!signedIn || !visitor || claimed) return
    setClaimed(true)
    supportChatApi.claim(visitor.token)
      .then((taken) => { queryClient.setQueryData(['support-chat', 'me'], taken) })
      .catch(() => { /* The visitor's conversation stays with its token; the account still chats as itself. */ })
      .finally(() => { storage.removeItem(visitorKey); setVisitor(null) })
  }, [signedIn, visitor, claimed, queryClient])

  useEffect(() => {
    if (open) endOfThread.current?.scrollIntoView?.({ block: 'end' })
  }, [open, thread.data?.messages.length])

  const messages: ChatMessage[] = thread.data?.messages ?? []
  const closed = thread.data?.status === 'CLOSED'

  async function send(event: FormEvent) {
    event.preventDefault()
    const message = text.trim()
    if (!message) { setError('Escribe tu mensaje.'); field.current?.focus(); return }
    setSending(true); setError('')
    try {
      let updated: ChatThread
      if (signedIn) {
        updated = await supportChatApi.sendMine(message, messageId.current)
      } else if (visitor) {
        updated = await supportChatApi.send(visitor.conversationId, visitor.token, message, messageId.current)
      } else {
        const started = await supportChatApi.start(message, messageId.current)
        const kept = { token: started.token, conversationId: started.conversationId }
        storage.setItem(visitorKey, JSON.stringify(kept))
        setVisitor(kept)
        updated = { conversationId: started.conversationId, status: 'OPEN', messages: started.messages }
      }
      queryClient.setQueryData(signedIn ? ['support-chat', 'me'] : ['support-chat', 'visitor', updated.conversationId], updated)
      messageId.current = crypto.randomUUID()
      setText('')
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'No pudimos enviar tu mensaje. Revisa tu conexión e intenta de nuevo.')
    } finally {
      setSending(false)
    }
  }

  return <div className="support-chat" data-open={open || undefined}>
    {open && <aside id={panelId} className="support-chat-panel" role="complementary" aria-labelledby={headingId}>
      <header className="support-chat-head">
        <div><h2 id={headingId}>Soporte BsPOS</h2><p>Te responde el asistente de BsPOS; lo que necesita a una persona lo atiende soporte.</p></div>
        <button type="button" className="icon-button" aria-label="Cerrar el chat" onClick={() => setOpen(false)}><Icon name="close" /></button>
      </header>
      <div className="support-chat-thread" aria-live="polite" aria-busy={thread.isLoading || undefined}>
        {messages.length === 0
          ? <p className="support-chat-empty">Cuéntanos qué necesitas: una cotización, una duda del programa o de tu cuenta. También puedes escribir a <a href={`mailto:${supportEmail}`}>{supportEmail}</a>{supportTelegram && <> o por <a href={telegramDirectUrl()} target="_blank" rel="noreferrer">Telegram</a></>}.</p>
          : <ol className="support-chat-messages">{messages.map((message) => <li key={message.id} data-author={message.author}>
            <span className="sr-only">{message.author === 'CUSTOMER' ? 'Tú' : 'Soporte'}, </span>
            <p>{message.body}</p>
            <time dateTime={message.createdAt}>{when.format(new Date(message.createdAt))}</time>
          </li>)}</ol>}
        {closed && <p className="support-chat-empty">Esta conversación se cerró. Si escribes de nuevo, la abrimos.</p>}
        <div ref={endOfThread} />
      </div>
      <form noValidate onSubmit={(event) => void send(event)} className="support-chat-form">
        <label htmlFor={fieldId} className="sr-only">Tu mensaje</label>
        <textarea id={fieldId} ref={field} rows={2} maxLength={2000} placeholder="Escribe tu mensaje" value={text} disabled={sending}
          onChange={(event) => setText(event.target.value)} aria-invalid={error ? true : undefined}
          onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); void send(event) } }} />
        <Button type="submit" busy={sending} aria-label="Enviar mensaje"><Icon name="arrow" /></Button>
        {error && <Notice>{error}</Notice>}
        <p className="field-hint">Al escribir aceptas el <Link to="/aviso-de-privacidad">Aviso de privacidad</Link>.</p>
      </form>
    </aside>}
    <div className="support-chat-actions">
      {!open && <TelegramLink className="support-chat-toggle support-chat-telegram"><Icon name="telegram" /><span>Telegram</span></TelegramLink>}
      <button type="button" className="support-chat-toggle" aria-expanded={open} aria-controls={open ? panelId : undefined} onClick={() => setOpen(!open)}>
        <Icon name={open ? 'close' : 'chat'} /><span>{open ? 'Cerrar' : 'Soporte'}</span>
      </button>
    </div>
  </div>
}
